import { useCallback, useEffect, useRef, useState } from "react";
import type { ConnectorType } from "@axio/graphql/generated/graphql";
import * as Sentry from "@sentry/react-native";

interface UseOAuthPopupOptions {
  authUrl?: string;
  connectorType?: ConnectorType;
  onTokenReceived?: (token: string) => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

interface UseOAuthPopupReturn {
  openOAuthPopup: (url: string, type?: ConnectorType) => Promise<string | null>;
  loading: boolean;
  error: string | null;
  token: string | null;
  reset: () => void;
}

/**
 * Hook to manage OAuth popup window communication.
 * Opens a popup for OAuth flow and listens for token via postMessage.
 *
 * @param options - Configuration for OAuth popup
 * @returns Object with methods and state for OAuth flow
 */
export function useOAuthPopup({
  authUrl,
  connectorType,
  onTokenReceived,
  onSuccess,
  onError,
}: UseOAuthPopupOptions = {}): UseOAuthPopupReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [popupOpenCount, setPopupOpenCount] = useState(0);
  const popupRef = useRef<Window | null>(null);
  const resolveRef = useRef<((token: string | null) => void) | null>(null);

  const handlePopupMessage = useCallback(
    (event: MessageEvent) => {
      // Only accept messages from our own origin (the oauth-callback page)
      if (event.origin !== window.location.origin) return;

      const message = event.data;

      // Check for success token message
      if (message.type === "oauth_token" && message.token) {
        const receivedToken = message.token;
        setToken(receivedToken);
        setLoading(false);
        setError(null);

        if (onTokenReceived) {
          onTokenReceived(receivedToken);
        }
        if (onSuccess) {
          onSuccess();
        }

        if (resolveRef.current) {
          resolveRef.current(receivedToken);
          resolveRef.current = null;
        }

        // Close popup after receiving token
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
      }

      if (message.type === "oauth_callback" && message.connectionId) {
        const receivedToken = message.connectionId;
        setToken(receivedToken);
        setLoading(false);
        setError(null);

        if (onTokenReceived) {
          onTokenReceived(receivedToken);
        }
        if (onSuccess) {
          onSuccess();
        }

        if (resolveRef.current) {
          resolveRef.current(receivedToken);
          resolveRef.current = null;
        }

        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
      }

      // Check for error message
      if (message.type === "oauth_error") {
        const errorMsg = message.error || "OAuth flow failed";
        setError(errorMsg);
        setLoading(false);

        if (onError) {
          onError(errorMsg);
        }

        if (resolveRef.current) {
          resolveRef.current(null);
          resolveRef.current = null;
        }

        // Close popup after error
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
      }
    },
    [onTokenReceived, onSuccess, onError]
  );

  useEffect(() => {
    window.addEventListener("message", handlePopupMessage);
    return () => {
      window.removeEventListener("message", handlePopupMessage);
    };
  }, [handlePopupMessage]);

  // Monitor popup window every 500ms.
  // Re-runs whenever a new popup is opened (popupOpenCount increments).

  useEffect(() => {
    if (!popupRef.current) return;

    const interval = setInterval(() => {
      const popup = popupRef.current;
      if (!popup) {
        clearInterval(interval);
        return;
      }

      // ── Popup was closed by user ──────────────────────────────────────────
      if (popup.closed) {
        setLoading(false);
        clearInterval(interval);
        resolveRef.current?.(null);
        resolveRef.current = null;
        return;
      }

      // ── URL polling fallback ──────────────────────────────────────────────

      try {
        const href = popup.location.href;

        // Still on the loading placeholder or blank — not redirected yet
        if (!href || href === "about:blank") return;

        const popupUrl = new URL(href);

        // Only act if the popup is back on our own origin
        if (popupUrl.origin !== window.location.origin) return;

        if (popupUrl.pathname === "/oauth-callback") return;

        // ── Popup landed on a non-callback page on our origin ─────────────

        clearInterval(interval);

        const connectionId =
          popupUrl.searchParams.get("connectionId") ||
          popupUrl.searchParams.get("connection_id") ||
          popupUrl.searchParams.get("code") ||
          "success";

        const errorParam = popupUrl.searchParams.get("error");
        try {
          popup.close();
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              source: "useOAuthPopup",
              step: "close_popup",
            },
          });
        }

        if (errorParam) {
          setError(errorParam);
          setLoading(false);
          if (onError) onError(errorParam);
          resolveRef.current?.(null);
          resolveRef.current = null;
        } else {
          setToken(connectionId);
          setLoading(false);
          setError(null);
          if (onSuccess) onSuccess();
          resolveRef.current?.(connectionId);
          resolveRef.current = null;
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            source: "useOAuthPopup",
            step: "url_polling",
          },
        });
      }
    }, 500);

    return () => clearInterval(interval);
  }, [popupOpenCount, onSuccess, onError]);

  const openOAuthPopup = useCallback(
    (url?: string, type?: ConnectorType): Promise<string | null> => {
      const effectiveUrl = url ?? authUrl;
      const effectiveType = type ?? connectorType;
      return new Promise((resolve) => {
        if (!effectiveUrl) {
          const connectorLabel = effectiveType ?? "connector";
          const errorMsg = `No authorization URL provided for ${connectorLabel}`;
          setError(errorMsg);
          if (onError) onError(errorMsg);
          resolve(null);
          return;
        }

        // Store resolver for message handler
        resolveRef.current = resolve;
        setLoading(true);
        setError(null);

        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        popupRef.current = window.open(
          effectiveUrl,
          `oauth_${effectiveType}`,
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );

        if (!popupRef.current) {
          const errorMsg = "Failed to open OAuth popup. Check popup blocker settings.";
          setError(errorMsg);
          setLoading(false);
          if (onError) onError(errorMsg);
          resolve(null);
          return;
        }

        popupRef.current.focus();

        // Timeout after 5 minutes if no response
        const timeoutId = setTimeout(() => {
          if (resolveRef.current) {
            const timeoutMsg = "OAuth flow timeout";
            setError(timeoutMsg);
            setLoading(false);
            if (onError) onError(timeoutMsg);
            resolveRef.current(null);
            resolveRef.current = null;
          }
          if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.close();
          }
        }, 300000);

        // Increment open count so the monitoring interval restarts
        setPopupOpenCount((c) => c + 1);

        // Clear timeout if token received before timeout
        const originalResolve = resolveRef.current;
        resolveRef.current = (tok) => {
          clearTimeout(timeoutId);
          originalResolve?.(tok);
        };
      });
    },
    [authUrl, connectorType, onError]
  );

  const reset = useCallback(() => {
    setToken(null);
    setError(null);
    setLoading(false);
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
    resolveRef.current = null;
  }, []);

  return {
    openOAuthPopup,
    loading,
    error,
    token,
    reset,
  };
}
